import { Box, Chip, Grid, useMediaQuery, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { BrandsContext } from "contexts/Brands";
import { useContext, useEffect } from "react";
import {
  BentoBrand,
  BentoBrandMetadataTags,
  MetadataType,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import { useAutoCompletePrediction } from "components/LocationAutocomplete/useAutoCompletePrediction";
import { BrandCardSource } from "features/Influencer/Discover/Results/schema";
import { DISCOVER_METADATA_TAGS } from "utils/localStorage";
import { makeDeepCopy } from "utils/updateLocalState";
import { useSearchBrand } from "utils/useSearchBrand";

interface Props {
  selectedBrand?: BentoBrand | null;
  justifyContent?: "flex-start" | "center";
  source?: BrandCardSource;
  noGrid?: boolean;
  clickable?: boolean;
}

const BrandMetadata = ({
  selectedBrand,
  justifyContent = "flex-start",
  source,
  noGrid = false,
  clickable = true,
}: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { setSelectedMetadataTags } = useContext(BrandsContext);
  const { handleAddGeopoint, handleClickOnTag } = useSearchBrand();
  const { geopoint, inputValue, setInputValue } =
    useAutoCompletePrediction(handleAddGeopoint);

  useEffect(() => {
    if (geopoint?.country_code && geopoint?.longitude && geopoint?.latitude) {
      setSelectedMetadataTags((prev) => {
        const exists = prev?.find((x) => x.value === inputValue);
        if (!exists) {
          prev.push({
            ...geopoint,
            type: MetadataType.location,
            value: inputValue,
          });
        }
        sessionStorage.setItem(DISCOVER_METADATA_TAGS, JSON.stringify(prev));
        return prev;
      });
      setInputValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geopoint]);

  if (!selectedBrand) return <></>;

  const displayedTags =
    isMobileScreen &&
    selectedBrand?.metadata &&
    selectedBrand?.metadata?.length > 2
      ? makeDeepCopy(selectedBrand?.metadata)?.splice(0, 2)
      : selectedBrand?.metadata;

  const selectedBrandData = (
    <>
      {displayedTags?.map((tag: BentoBrandMetadataTags, index: number) => (
        <Chip
          onClick={
            source === BrandCardSource.THREAD_BRAND || !clickable
              ? undefined
              : (e: CustomEvent) => handleClickOnTag(e, tag, setInputValue)
          }
          size="small"
          sx={{
            fontWeight: tag.type === MetadataType.location ? 600 : 400,
            backgroundColor: "white",
            border: `0.5px solid ${tag.type === MetadataType.location ? grey[900] : grey[600]}`,
            textTransform: "capitalize",
            fontSize: 11,
          }}
          label={
            <>
              {tag.type === MetadataType.location && (
                <Box
                  sx={{ mr: 0.5 }}
                  component="i"
                  className="fa-thin fa-location-dot"
                />
              )}
              {tag.value}
            </>
          }
          key={index}
        />
      ))}
    </>
  );

  if (noGrid) {
    return selectedBrandData;
  }

  return (
    <Grid
      container
      justifyContent={justifyContent}
      item
      xs={12}
      lg={
        source === BrandCardSource.SIMILAR_BRANDS ||
        source === BrandCardSource.ONBOARDING_BRAND ||
        source === BrandCardSource.THREAD_BRAND
          ? 12
          : 6
      }
      gap={0.5}
    >
      {selectedBrandData}
    </Grid>
  );
};

export default BrandMetadata;
